// overviewUtils.js

// Fixed column labels and base widths (same as your component)
export const FIXED_LABELS = new Set([
  "Format",
  "Basepack",
  "Basepack Desc",
  "DC",
  "CBU Code",
  "IC Flag",
  "IC Action",
  "Action Recommended",
]);

export const FIXED_WIDTHS = {
  "Format": 120,
  "Basepack": 90,
  "Basepack Desc": 200,
  "DC": 80,
  "CBU Code": 110,
  "IC Flag": 90,
  "IC Action":120,
  "Action Recommended": 200,
};

// Encoding helpers
export const decodeEntities = (s) =>
  String(s ?? "")
    .replaceAll(/&amp;amp;amp;amp;amp;amp;amp;lt;/gi, "&lt;")
    .replaceAll(/&amp;amp;amp;amp;amp;amp;amp;gt;/gi, "&gt;")
    .replaceAll(/&amp;amp;amp;amp;amp;amp;amp;amp;/gi, "&");

export const norm = (s) => decodeEntities(s).replaceAll(/\s+|_/g, "").toLowerCase();

// Data access helpers
export const getGroupObj = (rowObj, groupLabel) => {
  if (!rowObj) return undefined;
  if (rowObj[groupLabel] !== undefined) return rowObj[groupLabel];
  const target = norm(groupLabel);
  const foundKey = Object.keys(rowObj).find((k) => norm(k) === target);
  return foundKey ? rowObj[foundKey] : undefined;
};

export const getChildVal = (groupObj, key) => {
  if (!groupObj) return undefined;
  if (key in groupObj) return groupObj[key];
  const target = norm(key);
  const foundKey = Object.keys(groupObj).find((k) => norm(k) === target);
  return foundKey ? groupObj[foundKey] : undefined;
};

/* ✅ SUB-COLUMN HELPERS */
const SUB_COLUMN_PREFIX = "Sub_";

export const isSubColumn = (label) =>
  typeof label === "string" && label.startsWith(SUB_COLUMN_PREFIX);

export const normalizeColumnLabel = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replaceAll(/\s+/g, "")
    .replaceAll(/&amp;|&/g, "");

export const getSubColumnMeta = (label) => {
  if (!isSubColumn(label)) {
    return null;
  }

  const raw = label.slice(SUB_COLUMN_PREFIX.length);
  const separatorIndex = raw.lastIndexOf("_");

  if (separatorIndex < 0) {
    return null;
  }

  return {
    parentLabel: raw.slice(0, separatorIndex).trim(),
    childLabel: raw.slice(separatorIndex + 1).trim(),
  };
};

export const isSubColumnOfParent = (column, parentLabel) => {
  const meta = getSubColumnMeta(column?.label);

  if (!meta) {
    return false;
  }

  return (
    normalizeColumnLabel(meta.parentLabel) ===
    normalizeColumnLabel(parentLabel)
  );
};

// Parse customization -> groups & ordered children
export const parseCustomization = (columnsInput, fixedSet = FIXED_LABELS) => {
  const decode = (s) => decodeEntities(s);

  // Ordered visible column labels
  const orderedLabels = (Array.isArray(columnsInput) ? columnsInput : [])
    .filter((c) => c?.visibility)
    .sort((a, b) => (a?.DisplayOrder ?? 0) - (b?.DisplayOrder ?? 0))
    .map((c) => decode(c.label));

  const fixedLabels = orderedLabels.filter((lbl) => fixedSet.has(lbl));

  const groups = [];
  const childrenByGroup = new Map();
  const hiddenParents = new Set();

  // First pass: identify hidden parent columns (non-Sub_)
  (Array.isArray(columnsInput) ? columnsInput : []).forEach((c) => {
    if (!c) return;

    const label = decode(c.label);

    // Track non-Sub_ columns that are hidden
    if (!isSubColumn(label) && c.visibility === false && !fixedSet.has(label)) {
      hiddenParents.add(normalizeColumnLabel(label));
    }
  });

  // Second pass: collect parent and child columns from Sub_ definitions
  (Array.isArray(columnsInput) ? columnsInput : []).forEach((c) => {
    if (!c) return;

    const label = decode(c.label);
    if (fixedSet.has(label)) return;

    // Skip Sub_ columns in parent collection
    if (isSubColumn(label)) {
      // Process Sub_ column as a child definition
      const meta = getSubColumnMeta(label);
      if (meta && c.visibility !== false) {
        const parentLabel = decode(meta.parentLabel);
        const childLabel = decode(meta.childLabel);

        // Only add if parent is not hidden
        if (!hiddenParents.has(normalizeColumnLabel(parentLabel))) {
          const arr = childrenByGroup.get(parentLabel) || [];
          arr.push({ label: childLabel, order: c.DisplayOrder ?? 0 });
          childrenByGroup.set(parentLabel, arr);
        }
      }
      return;
    }

    // Non-Sub_ columns: add as parent only if visible
    if (c.visibility === false) return;

    const parent = decode(c.fieldsection || c.group || c.parent || "");
    if (!parent) {
      groups.push({ label, order: c.DisplayOrder ?? 0 });
      return;
    }

    const arr = childrenByGroup.get(parent) || [];
    arr.push({ label, order: c.DisplayOrder ?? 0 });
    childrenByGroup.set(parent, arr);
  });

  groups.sort((a, b) => a.order - b.order);
  const orderedGroups = groups.map((x) => x.label);

  const orderedChildren = new Map();
  Array.from(childrenByGroup.entries()).forEach(([g, arr]) => {
    arr.sort((a, b) => a.order - b.order);
    orderedChildren.set(g, arr.map((x) => x.label));
  });

  return { orderedGroups, orderedChildren, orderedLabels, fixedLabels };
};

// Build fixed columns with overrides
export const makeFixedColumns = (fixedLabels, baseWidths = FIXED_WIDTHS, overrides = {}) =>
  fixedLabels
    .map((lbl) => ({ label: lbl, width: overrides[lbl] ?? baseWidths[lbl] }))
    .filter(Boolean);

// Sum fixed pane width
export const sumFixedPaneWidth = (fixedColumns) =>
  fixedColumns.reduce((sum, c) => sum + (c.width || 100), 0);

// Child keys derivation with fallback to data sampling
export const deriveChildrenKeysFactory = (rows, totalRow, parsedMeta) => {
  return (label) => {
    const g = decodeEntities(label);

    // First: check if configured children exist (from Sub_ definitions)
    const fromConfig = parsedMeta?.orderedChildren?.get(g);
    if (fromConfig?.length) return fromConfig;

    // Fallback: derive from data sample
    const sampleObjs = [];
    if (Array.isArray(rows) && rows.length) {
      for (let i = 0; i < Math.min(rows.length, 10); i++) {
        const o = getGroupObj(rows[i], g);
        if (o && typeof o === "object") sampleObjs.push(o);
      }
    }
    if (totalRow) {
      const o = getGroupObj(totalRow, g);
      if (o && typeof o === "object") sampleObjs.push(o);
    }
    const keySet = new Set();
    sampleObjs.forEach((o) =>
      Object.keys(o ?? {}).forEach((k) => keySet.add(decodeEntities(k)))
    );
    return Array.from(keySet);
  };
};

// Column width heuristics
export const guessWidthPerCol = (groupLabel, childKeys = []) => {
  const g = decodeEntities(groupLabel);
  const n = norm(g);
  const hasPct = g.includes("%") || childKeys.some((k) => decodeEntities(k).includes("%"));
  if (n.includes("Demand-Index")) return 150;
  if (n.includes("lossreasons")) return 140;
  if (n.includes("stockoutloss")) return 150;
  if (hasPct) return 130;
  return 140;
};

// Build scrollable groups array (group + children + widthPerCol)
// Respects parent visibility, child visibility, DisplayOrder, and excludes empty groups
export const buildScrollableGroups = (groupLabels, deriveChildrenKeys) =>
  (groupLabels || [])
    .map((gl) => {
      const children = deriveChildrenKeys(gl);
      const widthPerCol = guessWidthPerCol(gl, children);
      return { group: gl, widthPerCol, children };
    })
    .filter((g) => (g.children?.length ?? 0) > 0);

// Value formatter
// export const formatValue = (val) => {
//   if (val === null || val === undefined) return "";
//   if (typeof val === "number") {
//     return Number.isFinite(val) ? val.toLocaleString("en-IN") : "";
//   }
//   return String(val);
// };

/* ✅ K / L / Cr FORMATTER */
export const formatToCappedUnit = (value) => {
  // Handle empty string, null, undefined explicitly
  if (value === null || value === undefined || value === "") return "";
  if (Number.isNaN(Number(value))) return "-";

  const numericValue = Number(value);
  const abs = Math.abs(numericValue);
  const sign = numericValue < 0 ? "-" : "";

  const format = (num, unit) =>
    `${sign}${Number(num.toFixed(1)).toString().replace(/\.0$/, "")} ${unit}`;

  if (abs < 1000) return `${numericValue}`;
  if (abs < 100000) return format(abs / 1000, "K");
  if (abs < 10000000) return format(abs / 100000, "L");
  return format(abs / 10000000, "Cr");
};

/* ✅ SMART FORMATTER (USED EVERYWHERE) */
export const formatValue = (value) => {
  // Handle empty string explicitly - do NOT convert to 0
  if (value === null || value === undefined || value === "") return "";

  const num = typeof value === "number" ? value : Number(value);

  if (!Number.isNaN(num)) {
    if (num >= 1000) {
      return formatToCappedUnit(num);
    }
    return num.toLocaleString("en-IN");
  }

  return value;
};